import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { PhilanthropiesAPI, Philanthropy, PhilanthropyCategory } from '../services/philanthropies.ts';
import { formatCurrency, applyCurrencyMask, parseCurrency, formatCurrencyInput } from '../utils/currency.ts';
import { ArrowLeftIcon, PencilIcon, DocumentTextIcon, CurrencyDollarIcon, HeartIcon } from '@heroicons/react/24/outline';

const categoryLabels: Record<PhilanthropyCategory, string> = {
  education: 'Educação',
  health: 'Saúde',
  environment: 'Meio Ambiente',
  other: 'Outros',
};

const PhilanthropyDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [item, setItem] = useState<Philanthropy | null>(null);
  const [donations, setDonations] = useState<any[]>([]);
  const [donationDate, setDonationDate] = useState<string>('');
  const [donationAmountDisplay, setDonationAmountDisplay] = useState<string>('');
  const [donationDescription, setDonationDescription] = useState<string>('');
  const [savingDonation, setSavingDonation] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDate, setEditingDate] = useState<string>('');
  const [editingAmountDisplay, setEditingAmountDisplay] = useState<string>('');
  const [editingDescription, setEditingDescription] = useState<string>('');
  const [savingEdit, setSavingEdit] = useState<boolean>(false);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      const data = await PhilanthropiesAPI.get(id);
      setItem(data);
      const rows = await PhilanthropiesAPI.listDonations(id);
      setDonations(rows);
    };
    load();
  }, [id]);

  if (!item) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/philanthropies')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar
          </button>

          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <HeartIcon className="h-8 w-8 mr-2 text-pink-600" />
            {item.description || 'Detalhe da Doação'}
          </h1>
          <p className="text-gray-600 mt-2">Detalhes da doação</p>
        </div>

        {/* Informações Básicas */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <DocumentTextIcon className="h-6 w-6 mr-2 text-primary-600" />
            Informações Básicas
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
              <p className="text-lg font-semibold text-gray-900">{categoryLabels[item.category]}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data da última doação</label>
              <p className="text-lg font-semibold text-gray-900">{item.donationDate ? new Date(item.donationDate).toLocaleDateString('pt-BR') : 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Criado em</label>
              <p className="text-lg font-semibold text-gray-900">{item.createdAt ? new Date(item.createdAt).toLocaleDateString('pt-BR') : 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Valores */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <CurrencyDollarIcon className="h-6 w-6 mr-2 text-primary-600" />
            Valores
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Valor disponível para doação</label>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(Number(item.availableAmount ?? item.amount) || 0)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Valor doado</label>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(Number(item.donatedAmount || 0))}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <DocumentTextIcon className="h-6 w-6 mr-2 text-primary-600" />
            Lançamentos de Doações
          </h3>

          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data *</label>
                <input type="date" value={donationDate} onChange={e => setDonationDate(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent border-gray-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor *</label>
                <input type="text" value={donationAmountDisplay} onChange={e => setDonationAmountDisplay(applyCurrencyMask(e.target.value))} placeholder="R$ 0,00" inputMode="decimal" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent border-gray-300" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
                <input type="text" value={donationDescription} onChange={e => setDonationDescription(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent border-gray-300" />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button disabled={savingDonation} onClick={async () => {
                if (!id) return;
                if (!donationDate || !donationAmountDisplay) return;
                setSavingDonation(true);
                try {
                  const amount = parseCurrency(donationAmountDisplay);
                  const updated = await PhilanthropiesAPI.addDonation(id, { date: donationDate, amount, description: donationDescription || null });
                  setItem(updated);
                  const rows = await PhilanthropiesAPI.listDonations(id);
                  setDonations(rows);
                  queryClient.invalidateQueries(['philanthropies']);
                  setDonationDate('');
                  setDonationAmountDisplay('');
                  setDonationDescription('');
                } finally { setSavingDonation(false); }
              }} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-60">{savingDonation ? 'Salvando...' : 'Adicionar Doação'}</button>
            </div>
          </div>

          <div className="border-t pt-4">
            {donations.length === 0 ? (
              <p className="text-gray-600">Nenhum lançamento de doação ainda.</p>
            ) : (
              <div className="space-y-3">
                {donations.map(d => (
                  <div key={d.id} className="flex items-center justify-between gap-3">
                    {editingId === d.id ? (
                      <>
                        <input type="date" value={editingDate} onChange={e => setEditingDate(e.target.value)} className="px-2 py-1 border rounded" />
                        <input type="text" value={editingAmountDisplay} onChange={e => setEditingAmountDisplay(applyCurrencyMask(e.target.value))} className="px-2 py-1 border rounded w-32" />
                        <input type="text" value={editingDescription} onChange={e => setEditingDescription(e.target.value)} className="px-2 py-1 border rounded flex-1" />
                        <button disabled={savingEdit} onClick={async () => {
                          if (!id || !editingId) return;
                          setSavingEdit(true);
                          try {
                            const amount = parseCurrency(editingAmountDisplay);
                            const updatedParent = await PhilanthropiesAPI.updateDonation(id, editingId, { date: editingDate || d.date, amount, description: editingDescription });
                            setItem(updatedParent);
                            const rows = await PhilanthropiesAPI.listDonations(id);
                            setDonations(rows);
                            queryClient.invalidateQueries(['philanthropies']);
                            setEditingId(null);
                            setEditingDate('');
                            setEditingAmountDisplay('');
                            setEditingDescription('');
                          } finally { setSavingEdit(false); }
                        }} className="px-3 py-1 bg-primary-600 text-white rounded">Salvar</button>
                        <button onClick={() => { setEditingId(null); }} className="px-3 py-1 border rounded">Cancelar</button>
                      </>
                    ) : (
                      <>
                        <span className="text-sm text-gray-500">{new Date(d.date).toLocaleDateString('pt-BR')}</span>
                        <span className="text-sm font-medium text-gray-900">{formatCurrency(Number(d.amount) || 0)}</span>
                        <span className="text-sm text-gray-600 truncate max-w-[40%]">{d.description || ''}</span>
                        <button onClick={() => { setEditingId(d.id); setEditingDate(d.date); setEditingAmountDisplay(formatCurrencyInput(Number(d.amount || 0))); setEditingDescription(d.description || ''); }} className="px-3 py-1 border rounded">Editar</button>
                        <button onClick={async () => { if (!id) return; const updatedParent = await PhilanthropiesAPI.deleteDonation(id, d.id); setItem(updatedParent); const rows = await PhilanthropiesAPI.listDonations(id); setDonations(rows); queryClient.invalidateQueries(['philanthropies']); }} className="px-3 py-1 border border-red-300 text-red-700 rounded">Excluir</button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 mt-8">
          <button
            type="button"
            onClick={() => navigate(`/philanthropies/${item.id}/edit`)}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center"
          >
            <PencilIcon className="h-5 w-5 mr-2" />
            Editar Doação
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhilanthropyDetail;
